import React, { useState, useCallback, useEffect } from "react";
// import { useTranslation } from "react-i18next";
import Badge from "../badge";
import productIcon from "@surgia/ui/icons/catalog/hubProduct.svg";
import Button from "../button";
import shoppingCartIcon from "@surgia/ui/icons/catalog/hub/shoppingCartPurple.svg";
import hoveredShoppingCartIcon from "@surgia/ui/icons/catalog/hub/shoppingCartWhite.svg";
import deleteIcon from "@surgia/ui/icons/actions/delete.svg";
import plusIcon from "@surgia/ui/icons/actions/plus.svg";
import minusIcon from "@surgia/ui/icons/actions/minus.svg";
import { useUpdatePurchaseRequest } from "../../../../../apps/hub-surgia/src/hooks/useUpdatePurchaseRequest";
import { toast } from "sonner";
import { useDeletePurchaseRequestItem } from "../../../../../apps/hub-surgia/src/hooks/useDeletePurchaseRequestItem";

interface ProductDisplayCardProps {
  product: {
    tenant_product_id: string;
    catalog_product_id: string;
    name: string;
    category: any;
    specifications: string;
    brand: {
      name: string;
      logo?: string;
    };
    price: {
      amount: number;
      currency: string;
    };
    image: string;
    hasVariants: boolean;
    variantsCount?: number;
    minimum_quantity: number;
    maximum_quantity: number;
    count_in_branch_pr: number;
    feature_values: any[];
  };
  onClick?: () => void;
  className?: string;
  permissions: any[];
  showProductVariations?: (product: any) => void;
  selectedBranchId?: string;
  tenant_id?: string;
  draftPurchaseRequestID?: string;
}

const ProductDisplayCard: React.FC<ProductDisplayCardProps> = ({
  product,
  onClick,
  className = "",
  showProductVariations,
  selectedBranchId,
  tenant_id,
  draftPurchaseRequestID,
}) => {
  //   const { t } = useTranslation();
  const permissions = JSON.parse(localStorage.getItem("permissions") || "[]");
  const [isButtonHovered, setIsButtonHovered] = useState(false);

  // Helper function to strip HTML tags from description
  const stripHtmlTags = (html: string): string => {
    if (!html) return "";
    const tmp = document.createElement("DIV");
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || "";
  };

  const updatePurchaseRequestMutation = useUpdatePurchaseRequest({
    branchId: selectedBranchId || "",
    componentId: import.meta.env.VITE_PUBLIC_COMPONENT_ID || "",
    tenantId: tenant_id || "",
    draftPurchaseRequestID: draftPurchaseRequestID
      ? draftPurchaseRequestID?.toString()
      : "",
    minQuantity: product.minimum_quantity,
    maxQuantity: product.maximum_quantity,
  });
  const deletePurchaseRequestItemMutation = useDeletePurchaseRequestItem({
    branchId: selectedBranchId || "",
    componentId: import.meta.env.VITE_PUBLIC_COMPONENT_ID || "",
    tenantId: tenant_id!,
  });
  // Add handler for decrement button
  const handleDecrement = async () => {
    if (product?.count_in_branch_pr - 1 < product?.minimum_quantity) {
      toast.error("Minimum quantity reached");
      return;
    }
    if (product?.count_in_branch_pr > 1) {
      await updatePurchaseRequestMutation.mutateAsync({
        branchId: selectedBranchId!,
        componentId: import.meta.env.VITE_PUBLIC_COMPONENT_ID || "",
        tenantId: tenant_id!,
        body: {
          tenant_product_id: product?.tenant_product_id,
          quantity: product?.count_in_branch_pr - 1,
        },
      });
    }
  };
  const [inputValue, setInputValue] = useState(
    product?.count_in_branch_pr?.toString()
  );

  // Use useRef to store the timeout ID and create a stable debounced function
  const timeoutRef = React.useRef<NodeJS.Timeout | undefined>(undefined);

  const debouncedUpdate = useCallback(
    async (newQuantity: number) => {
      // Clear any existing timeout
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      // Set new timeout
      timeoutRef.current = setTimeout(async () => {
        // Validate quantity bounds
        if (
          newQuantity < product?.minimum_quantity ||
          newQuantity > product?.maximum_quantity
        ) {
          toast.error(
            `Quantity must be between ${product?.minimum_quantity} and ${product?.maximum_quantity}`
          );
          return;
        }

        // Update the purchase request with new quantity
        await updatePurchaseRequestMutation.mutateAsync({
          branchId: selectedBranchId!,
          componentId: import.meta.env.VITE_PUBLIC_COMPONENT_ID || "",
          tenantId: tenant_id!,
          body: {
            tenant_product_id: product?.tenant_product_id,
            quantity: newQuantity,
          },
        });
      }, 2000);
    },
    [
      product?.minimum_quantity,
      product?.maximum_quantity,
      product?.tenant_product_id,
      selectedBranchId,
      tenant_id,
      updatePurchaseRequestMutation,
    ]
  );

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  // Update input value when product count changes (e.g., from increment/decrement)
  useEffect(() => {
    setInputValue(product?.count_in_branch_pr?.toString());
  }, [product?.count_in_branch_pr]);

  // Add handler for increment button
  const handleIncrement = async () => {
    if (product?.count_in_branch_pr + 1 > product?.maximum_quantity) {
      toast.error("Maximum quantity reached");
      return;
    }
    await updatePurchaseRequestMutation.mutateAsync({
      branchId: selectedBranchId!,
      componentId: import.meta.env.VITE_PUBLIC_COMPONENT_ID || "",
      tenantId: tenant_id!,
      body: {
        tenant_product_id: product?.tenant_product_id,
        quantity: product?.count_in_branch_pr + 1,
      },
    });
  };

  // Add handler for "Add to Cart" button
  const handleAddToCart = async () => {
    if (product.hasVariants) {
      showProductVariations?.(product);
    } else {
      await updatePurchaseRequestMutation.mutateAsync({
        branchId: selectedBranchId!,
        componentId: import.meta.env.VITE_PUBLIC_COMPONENT_ID || "",
        tenantId: tenant_id!,
        body: {
          tenant_product_id: product?.tenant_product_id,
          quantity: product?.minimum_quantity,
        },
      });
    }
  };
  const handleDelete = async () => {
    await deletePurchaseRequestItemMutation.mutateAsync({
      branchId: selectedBranchId!,
      itemId: String(product?.tenant_product_id),
      componentId: import.meta.env.VITE_PUBLIC_COMPONENT_ID || "",
      tenantId: tenant_id!,
    });
  };

  const descriptionText = stripHtmlTags(product.specifications);

  return (
    <div
      className={`bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden cursor-pointer hover:shadow-md transition-shadow ${className}`}
      onClick={onClick}
    >
      {/* Top Section - Product Visual and Key Features */}
      <div
        className="relative p-4  bg-contain bg-center bg-no-repeat h-[220px] shadow-[0_0_0_0.5px_rgba(0,0,0,0.1)]"
        style={{
          backgroundImage: `url(${product.image ?? productIcon})`,
        }}
      >
        {/* Variants Included Badge */}
        {product.hasVariants && (
          <div className="absolute top-3 left-3 z-10">
            <Badge
              variant="POLY_CLINIC"
              size="sm"
              className="bg-purple-100 text-purple-700 border-purple-200"
            >
              <div className="flex items-center gap-1">
                <div className="w-1.5 h-1.5 bg-primary rounded-full"></div>
                <span className="text-xs font-medium">Variants Included</span>
              </div>
            </Badge>
          </div>
        )}
      </div>

      {/* Bottom Section - Product Details and Pricing */}
      <div className="p-4 space-y-3">
        {/* Category Tag */}
        <div className="flex items-center gap-2 overflow-x-auto hide-scrollbar">
          <Badge
            variant="default"
            size="sm"
            className="bg-gray-100 text-gray-700 border-gray-200 flex-shrink-0"
          >
            {product.category.surgiaFirstCategory}
          </Badge>
          {product.category.surgiaSecondCategory && (
            <Badge
              variant="default"
              size="sm"
              className="bg-gray-100 text-gray-700 border-gray-200 flex-shrink-0"
            >
              {product.category.surgiaSecondCategory}
            </Badge>
          )}
          {product.category.surgiaThirdCategory && (
            <Badge
              variant="default"
              size="sm"
              className="bg-gray-100 text-gray-700 border-gray-200 flex-shrink-0"
            >
              {product.category.surgiaThirdCategory}
            </Badge>
          )}
        </div>
        {/* Product Name */}
          <h3 className="text-lg font-bold text-gray-900 leading-tight max-w-full truncate">
            {product.name}
          </h3>
        {/* Product Description */}
        {product.specifications && (
          <div className="relative group">
            <p className="text-sm text-gray-600 leading-relaxed line-clamp-3 max-w-full">
              {descriptionText}
            </p>
            {/* Custom Tooltip - Only shows on hover */}
            <div className="absolute left-0 bottom-full mb-3 z-[100] w-80 max-w-[calc(100vw-2rem)] opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 ease-in-out pointer-events-none">
              <div className="bg-gray-900 text-white text-sm rounded-lg shadow-2xl px-4 py-3 relative">
                <p className="leading-relaxed whitespace-normal break-words">
                  {descriptionText}
                </p>
              </div>
            </div>
          </div>
        )}
        {/* Brand Information */}
        <div className="flex items-center gap-2">
          {product.brand.logo ? (
            <img
              src={product.brand.logo}
              alt={product.brand.name}
              className="w-6 h-6 rounded-full object-cover"
              onError={(e) => {
                e.currentTarget.style.display = "none";
              }}
            />
          ) : (
            <div className="w-6 h-6 rounded-full bg-red-500 flex items-center justify-center">
              <span className="text-white text-xs font-bold">
                {product.brand.name.charAt(0).toUpperCase()}
              </span>
            </div>
          )}
          <span className="text-sm text-gray-700">{product.brand.name}</span>
        </div>
        {/* Feature Values Section */}

        <div className="w-full">
          <span className="text-xs text-gray-600 truncate block">
            {product.feature_values
              .filter(
                (feature) => feature.feature_Name.toLowerCase() !== "brand"
              )
              .map(
                (feature) =>
                  `${feature.feature_Name} ${feature.featureVariant.variant_name}`
              )
              .join(" - ")}
          </span>
        </div>

        {/* Price */}
        {permissions.includes("VIEW_PRODUCT_PRICE") && (
          <div className="pt-2 text-xl font-bold text-gray-900 flex gap-1 items-baseline">
            <p className="">{product.price.amount.toLocaleString()}</p>
            <p className="font-normal text-[14px]  text-gray-500">
              {product.price.currency}
            </p>
          </div>
        )}

        {product?.count_in_branch_pr > 0 && !product?.hasVariants ? (
          <div className="flex items-center gap-2 justify-between">
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleDelete();
                // setProductCount(0);
              }}
            >
              <img src={deleteIcon} alt="delete" />
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();

                handleDecrement();
              }}
            >
              <img src={minusIcon} alt="minus" />
            </button>

            {/* Input field replacing the span */}
            <div className="flex flex-col items-start p-0 gap-1.5 w-[220px] h-10">
              <input
                type="number"
                value={inputValue}
                onChange={(e) => {
                  e.stopPropagation();
                  const newValue = e.target.value;

                  // Only allow numeric characters and empty string
                  if (newValue === "" || /^\d+$/.test(newValue)) {
                    setInputValue(newValue);

                    // Only validate and update if it's a valid number
                    const newQuantity = parseInt(newValue);
                    if (!isNaN(newQuantity) && newValue !== "") {
                      debouncedUpdate(newQuantity);
                    }
                  }
                }}
                onKeyDown={(e) => {
                  // Prevent non-numeric keys from being entered
                  if (
                    e.key !== "Backspace" &&
                    e.key !== "Delete" &&
                    e.key !== "Tab" &&
                    e.key !== "Enter" &&
                    e.key !== "ArrowLeft" &&
                    e.key !== "ArrowRight" &&
                    e.key !== "ArrowUp" &&
                    e.key !== "ArrowDown" &&
                    !/^\d$/.test(e.key)
                  ) {
                    e.preventDefault();
                  }
                }}
                className="w-full h-10 px-3 border border-[#D5D7DA] rounded-md text-[16px]  text-[#181D27] text-center focus:outline-none focus:ring-2  focus:border-transparent"
                style={{
                  flex: "none",
                  order: 1,
                  flexGrow: 1,
                }}
                // Add min and max attributes to the input
                min={product.minimum_quantity}
                max={product.maximum_quantity}
              />
            </div>

            <button
              onClick={(e) => {
                e.stopPropagation();
                handleIncrement();
              }}
            >
              <img src={plusIcon} alt="add" />
            </button>
          </div>
        ) : (
          <Button
            variant="cart"
            className="!w-full"
            startIcon={
              isButtonHovered ? hoveredShoppingCartIcon : shoppingCartIcon
            }
            onClick={handleAddToCart}
            onMouseEnter={() => setIsButtonHovered(true)}
            onMouseLeave={() => setIsButtonHovered(false)}
          >
            {updatePurchaseRequestMutation?.isPending ||
            updatePurchaseRequestMutation?.allTenantProductsInvalidationPending
              ? "Adding to PR"
              : "Add to PR"}
          </Button>
        )}
      </div>
    </div>
  );
};

export default ProductDisplayCard;
